/**
 * Calibration tree expand-state — persisted across in-app navigation so
 * an HR user who clicks into a per-review calibration screen and then
 * returns to the tray finds the tree exactly as they left it.
 *
 * Design choices:
 *   · localStorage (not sessionStorage) so a hard refresh, closed tab, or
 *     accidental browser restart doesn't lose the state. We explicitly
 *     clear on sign-out to keep the next user on this device from
 *     inheriting it.
 *   · Module-level singleton state — every <CalibrationDeptNode> shares
 *     the same Map via the composable. Avoids prop-drilling a state
 *     object through the recursive component.
 *   · Keyed by the manager employee's empId. That's stable across
 *     re-renders, unique per department node, and survives data refresh.
 *   · `ensureSeeded(rootKeys)` — fired by the tray page on first visit
 *     (state empty). Opens the top-level node(s) so HR doesn't land on
 *     a fully-collapsed tree. After the user manually toggles anything,
 *     state is no longer "empty" and seeding becomes a no-op.
 */
import { ref, watch } from 'vue'

const STORAGE_KEY = 'medlive-hris.calibration.expand'

function load(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) as Record<string, boolean> : {}
  } catch {
    return {}
  }
}

// Singleton state, lazily initialized from localStorage on first import.
const state = ref<Record<string, boolean>>(load())

watch(state, (v) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v))
  } catch {
    // Storage quota exceeded or unavailable — silently ignore. The
    // tree still works; it just won't survive a refresh.
  }
}, { deep: true })

export function useCalibrationExpandState() {
  function isOpen(key: string): boolean {
    return state.value[key] === true
  }
  function setOpen(key: string, value: boolean): void {
    // Replace the map (rather than mutate in place) so Vue picks up the
    // change deterministically. `deep: true` on the watch above also
    // handles in-place mutation, but explicit replacement is clearer.
    state.value = { ...state.value, [key]: value }
  }
  /**
   * Batch update for "Collapse all" / future "Expand all" — applies the
   * given map of key→open in one reactive write rather than N. The map
   * is MERGED with existing state; pass an empty map to no-op.
   */
  function setMany(updates: Record<string, boolean>): void {
    state.value = { ...state.value, ...updates }
  }
  /**
   * Seed the top-level roots as open on first visit. No-op once the
   * user has toggled anything (state is no longer empty), so we don't
   * trample their manual closes.
   */
  function ensureSeeded(rootKeys: string[]): void {
    if (Object.keys(state.value).length > 0) return
    const next: Record<string, boolean> = {}
    for (const k of rootKeys) next[k] = true
    state.value = next
  }
  /**
   * Wipe all expand state. Called from auth.signOut() so the next user
   * on this device gets a fresh tree.
   */
  function clearAll(): void {
    state.value = {}
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ok
    }
  }
  return { isOpen, setOpen, setMany, ensureSeeded, clearAll }
}
